import React, { useEffect, useState } from "react";
import EnumInput from "../../../../../misc/EnumInput";
import { FormGroup, Label, Input, Card, CardHeader, CardBody, Collapse, Button, FormFeedback, UncontrolledTooltip } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslation } from 'react-i18next';
import { CoreEdlevoConfiguration, EdlevoGroupType, EdlevoGroupTypeOptions, EdlevoMentorGroupTypeOptions, EdlevoSchoolTypeOptions, EdlevoSensitiveFieldsOptions, EdlevoMentorGroupType, EdlevoSensitiveFields } from "../../../../../../models/CoreEdlevoModels";
import i18n from 'i18next';


export const validator = (configuration: CoreEdlevoConfiguration, active: boolean) => {
    const validation = [];

    if (active) {
        if (!configuration.baseAddress || configuration.baseAddress === "" || configuration.baseAddress.charAt(configuration.baseAddress.length - 1) !== '/') {
            validation.push('baseAddress');
        }
        if (!configuration.licenseKey || configuration.licenseKey === "") {
            validation.push('licenseKey');
        }
        if (!configuration.certificateThumbprint || configuration.certificateThumbprint === "") {
            validation.push('certificate');
        }
        if (configuration.schoolTypes <= 0) {
            validation.push('schoolTypes')
        }
    }

    if (configuration.onlyIncludeSchoolsWithMunicipalityCode && !configuration.municipalityCode) {
        validation.push('municipalityCode');
    }
    if (configuration.startDateOffset === undefined) {
        validation.push('startDateOffsetRequired');
    }
    if (configuration.startDateOffset < 0 || configuration.startDateOffset > 90) {
        validation.push('startDateOffset');
    }

    return validation;
}

const hasDeselectedType = (originalValue: number, updatedValue: undefined | number, type: number) => {
    return updatedValue !== undefined && (originalValue & type) === type && (updatedValue & type) === 0;
}

export const warning = (originalConfiguration: CoreEdlevoConfiguration, updatedConfiguration: CoreEdlevoConfiguration) => {
    const warnings: string[] = [];

    if (hasDeselectedType(originalConfiguration.groupType, updatedConfiguration.groupType, EdlevoGroupType.EducationGroup)
        || hasDeselectedType(originalConfiguration.groupType, updatedConfiguration.groupType, EdlevoGroupType.ActivityBasedGroup)) {
        warnings.push(i18n.t('edlevoConfiguration.activities.warningMessage', { ns: 'configuration' }))
    }

    if (hasDeselectedType(originalConfiguration.mentorGroupType, updatedConfiguration.mentorGroupType, EdlevoMentorGroupType.GroupBased)
        || hasDeselectedType(originalConfiguration.mentorGroupType, updatedConfiguration.mentorGroupType, EdlevoMentorGroupType.ClassBased)) {
        warnings.push(i18n.t('edlevoConfiguration.mentors.warningMessage', { ns: 'configuration' }))
    }

    if (hasDeselectedType(originalConfiguration.sensitiveFields, updatedConfiguration.sensitiveFields, EdlevoSensitiveFields.Custodian)) {
        warnings.push(i18n.t('edlevoConfiguration.sensitiveFields.warningMessage', { ns: 'configuration' }));
    }

    return warnings;
}

interface EdlevoConfigurationProps {
    configuration: CoreEdlevoConfiguration
    disabled: boolean
    updatePart: (part: Partial<CoreEdlevoConfiguration>) => void
    active: boolean
    onValidChange: (arg: boolean) => void
}

const EdlevoConfiguration = (props: EdlevoConfigurationProps) => {
    const { t } = useTranslation(['configuration', 'common']);

    const [open, setOpen] = useState<boolean>(true);

    const validation = validator(props.configuration, props.active);

    useEffect(() => {
        props.onValidChange(validation.length ? true : false)
    }, [props.configuration, props.active]);

    const updateMunicipalitySchool = (prop: keyof CoreEdlevoConfiguration, value: boolean | string) => {
        if (value === false) {
            if (prop === "onlyIncludeGovernedByMunicipal") {
                props.updatePart({ onlyIncludeGovernedByMunicipal: false, onlyIncludeSchoolsWithMunicipalityCode: false, municipalityCode: undefined })
            }
            if (prop === "onlyIncludeSchoolsWithMunicipalityCode") {
                props.updatePart({ onlyIncludeSchoolsWithMunicipalityCode: false, municipalityCode: undefined })
            }
        } else {
            props.updatePart({ [prop]: value });
        }
    }

    return (
        <Card className="edit_module">
            <CardHeader tag="h2">
                <Button outline color="secondary" style={{ width: '37px', float: 'right' }} onClick={() => setOpen(!open)}>{open ? '-' : '+'}</Button>
                <span>
                    {t('modules.TietoEducation.title', { ns: 'common' })}
                    <a href="https://flowsync.se/help/modules/import/edlevo" target="_blank"><FontAwesomeIcon className="documentation-icon" title={t('showHelp', { ns: 'common' })} icon="question-circle" /></a>
                </span>
            </CardHeader>
            <Collapse isOpen={open}>
                <CardBody>
                    <h5>{t('edlevoConfiguration.accessHeader')}</h5>
                    <FormGroup>
                        <Label for="baseAddress">{t('edlevoConfiguration.baseAddress.label', { ns: 'configuration' })}</Label>
                        <Input
                            type="text"
                            name="baseAddress"
                            id="baseAddress"
                            placeholder={t('edlevoConfiguration.baseAddress.placeholder', { ns: 'configuration' })}
                            value={props.configuration.baseAddress || ''}
                            disabled={props.disabled}
                            onChange={(e) => props.updatePart({ baseAddress: e.target.value })}
                            invalid={validation.indexOf('baseAddress') !== -1}
                            autoComplete="off"
                        />
                        <FormFeedback>{t('edlevoConfiguration.baseAddress.formFeedback', { ns: 'configuration' })}</FormFeedback>
                    </FormGroup>
                    <FormGroup>
                        <Label for="licenseKey">{t('edlevoConfiguration.licenseKey.label', { ns: 'configuration' })}</Label>
                        <Input
                            type="text"
                            name="licenseKey"
                            id="licenseKey"
                            placeholder={t('edlevoConfiguration.licenseKey.placeholder', { ns: 'configuration' })}
                            value={props.configuration.licenseKey || ''}
                            disabled={props.disabled}
                            onChange={(e) => props.updatePart({ licenseKey: e.target.value })}
                            invalid={validation.indexOf('licenseKey') !== -1}
                            autoComplete="off"
                        />
                        <FormFeedback>{t('edlevoConfiguration.licenseKey.formFeedback', { ns: 'configuration' })}</FormFeedback>
                    </FormGroup>
                    <FormGroup>
                        <Label for="certificateThumbprint">{t('edlevoConfiguration.certificateThumbprint.label', { ns: 'configuration' })}</Label>
                        <Input
                            type="text"
                            name="certificateThumbprint"
                            id="certificateThumbprint"
                            placeholder={t('edlevoConfiguration.certificateThumbprint.placeholder', { ns: 'configuration' })}
                            value={props.configuration.certificateThumbprint || ''}
                            disabled={props.disabled}
                            invalid={validation.indexOf('certificate') !== -1}
                            onChange={(e) => props.updatePart({ certificateThumbprint: e.target.value })}
                            autoComplete="off"
                        />
                        <FormFeedback>{t('edlevoConfiguration.certificateThumbprint.formFeedback', { ns: 'configuration' })}</FormFeedback>
                    </FormGroup>

                    <FormGroup>
                        <h5>{t('edlevoConfiguration.generalHeader', { ns: 'configuration' })}</h5>
                        <Label for="startDateOffset">{t('edlevoConfiguration.startDateOffset.label', { ns: 'configuration' })}</Label>
                        <Input
                            type="number"
                            name="startDateOffset"
                            id="startDateOffset"
                            placeholder={t('edlevoConfiguration.startDateOffset.placeholder', { ns: 'configuration' })}
                            value={props.configuration.startDateOffset}
                            disabled={props.disabled}
                            onChange={e => props.updatePart({ startDateOffset: isNaN(parseInt(e.target.value, 10)) ? undefined : parseInt(e.target.value, 10) })}
                            required
                            invalid={validation.indexOf('startDateOffsetRequired') !== -1 || validation.indexOf('startDateOffset') !== -1}
                        />
                        <FormFeedback>{validation.indexOf('startDateOffsetRequired') !== -1
                            && t('edlevoConfiguration.startDateOffset.formFeedback', { ns: 'configuration' })}
                            {validation.indexOf('startDateOffset') !== -1
                                && t('edlevoConfiguration.startDateOffset.invalidNumbers', { ns: 'configuration' })}
                        </FormFeedback>
                    </FormGroup>

                    <EnumInput
                        title={t('edlevoConfiguration.sensitiveFields.title', { ns: 'configuration' })}
                        name='sensitiveFields'
                        value={props.configuration.sensitiveFields}
                        options={EdlevoSensitiveFieldsOptions.map(key => ({
                            value: key,
                            text: t(`edlevoConfiguration.sensitiveFields.options.${key}`, { ns: 'configuration' })
                        }))}
                        onChange={(name, value) => props.updatePart({ [name]: value })}
                        disabled={props.disabled}
                        multi={true}
                    />

                    <FormGroup>
                        <h5>{t('edlevoConfiguration.includePersonsWithProtectedAddressInformation.title', { ns: 'configuration' })}</h5>
                        <FormGroup check name="includePersonsRegardlessOfPrivacy">
                            <Label check style={{ display: 'block', marginBottom: '8px' }}>
                                <Input
                                    type="checkbox"
                                    name="includePersonsRegardlessOfPrivacy"
                                    id="includePersonsRegardlessOfPrivacy"
                                    disabled={props.disabled}
                                    checked={props.configuration.includePersonsRegardlessOfPrivacy}
                                    onChange={(e) => props.updatePart({ includePersonsRegardlessOfPrivacy: e.target.checked })}
                                />
                                {t('edlevoConfiguration.includePersonsRegardlessOfPrivacy.label', { ns: 'configuration' })}
                            </Label>
                        </FormGroup>
                        <FormGroup check name="includePersonsWithProtectedAddressInformation">
                            <Label
                                check
                                id="includePersonsWithProtectedAddressInformationLabel"
                                style={{ display: 'inline-block', marginBottom: '8px' }}>
                                <Input
                                    type="checkbox"
                                    name="includePersonsWithProtectedAddressInformation"
                                    id="includePersonsWithProtectedAddressInformation"
                                    disabled={props.disabled || props.configuration.includePersonsRegardlessOfPrivacy}
                                    checked={props.configuration.includePersonsWithProtectedAddressInformation}
                                    onChange={(e) => props.updatePart({ includePersonsWithProtectedAddressInformation: e.target.checked })}
                                />
                                {t('edlevoConfiguration.includePersonsWithProtectedAddressInformation.label', { ns: 'configuration' })}
                            </Label>
                            {props.configuration.includePersonsRegardlessOfPrivacy && (
                                <UncontrolledTooltip target="includePersonsWithProtectedAddressInformationLabel" placement="bottom-start">
                                    {t('edlevoConfiguration.includePersonsWithProtectedAddressInformation.disabledTooltip', { ns: 'configuration' })}
                                </UncontrolledTooltip>
                            )}
                        </FormGroup>
                    </FormGroup>

                    <h5>{t('edlevoConfiguration.activities.header', { ns: 'configuration' })} <a href="https://flowsync.se/help/modules/import/edlevo#aktiviteter" target="_blank"><FontAwesomeIcon title={t('showHelp', { ns: 'common' })} icon="question-circle" /></a></h5>
                    <EnumInput
                        title={t('edlevoConfiguration.activities.title', { ns: 'configuration' })}
                        name='groupType'
                        titleSmall={true}
                        value={props.configuration.groupType}
                        options={EdlevoGroupTypeOptions.map(key => ({
                            value: key,
                            text: t(`edlevoConfiguration.activities.options.${key}`, { ns: 'configuration' })
                        }))}
                        onChange={(name, value) => props.updatePart({ [name]: value })}
                        disabled={props.disabled}
                        multi={true}
                    />

                    <FormGroup>
                        <Label for="notesFilter">
                            {t('edlevoConfiguration.notesFilter.label', { ns: 'configuration' })}
                            <br />
                            <i>{t('edlevoConfiguration.notesFilter.info', { ns: 'configuration' })}</i>
                        </Label>
                        <Input
                            type="text"
                            name="notesFilter"
                            id="notesFilter"
                            placeholder={t('edlevoConfiguration.notesFilter.placeholder', { ns: 'configuration' })}
                            value={props.configuration.notesFilter || ''}
                            disabled={props.disabled}
                            onChange={(e) => props.updatePart({ notesFilter: e.target.value })}
                            autoComplete="off"
                        />
                    </FormGroup>

                    <h5>{t('edlevoConfiguration.mentors.header', { ns: 'configuration' })}</h5>
                    <EnumInput
                        title={t('edlevoConfiguration.mentors.title', { ns: 'configuration' })}
                        name='mentorGroupType'
                        titleSmall={true}
                        value={props.configuration.mentorGroupType}
                        options={EdlevoMentorGroupTypeOptions.map(key => ({
                            value: key,
                            text: t(`edlevoConfiguration.mentors.options.${key}`, { ns: 'configuration' })
                        }))}
                        onChange={(name, value) => props.updatePart({ [name]: value })}
                        disabled={props.disabled}
                        multi={true}
                    />

                    <FormGroup>
                        <h5>{t('edlevoConfiguration.schools.header', { ns: 'configuration' })}</h5>
                        <FormGroup check name="onlyIncludeGovernedByMunicipal">
                            <Label check style={{ display: 'block', marginBottom: '8px' }}>
                                <Input
                                    type="checkbox"
                                    name="onlyIncludeGovernedByMunicipal"
                                    id="onlyIncludeGovernedByMunicipal"
                                    disabled={props.disabled}
                                    checked={props.configuration.onlyIncludeGovernedByMunicipal}
                                    onChange={(e) => updateMunicipalitySchool('onlyIncludeGovernedByMunicipal', e.target.checked)}
                                />
                                {t('edlevoConfiguration.schools.onlyIncludeGovernedByMunicipalLabel', { ns: 'configuration' })}
                            </Label>
                        </FormGroup>

                        <FormGroup check className="govern_schools">
                            <Label check style={{ minWidth: '200px', marginBottom: '8px', marginTop: '3px' }}>
                                <Input
                                    type="checkbox"
                                    name="onlyIncludeSchoolsWithMunicipalityCode"
                                    id="onlyIncludeSchoolsWithMunicipalityCode"
                                    disabled={!props.configuration.onlyIncludeGovernedByMunicipal || props.disabled}
                                    checked={props.configuration.onlyIncludeSchoolsWithMunicipalityCode}
                                    onChange={(e) => updateMunicipalitySchool('onlyIncludeSchoolsWithMunicipalityCode', e.target.checked)}
                                />
                                {t('edlevoConfiguration.schools.onlyIncludeSchoolsWithMunicipalityCodeLabel', { ns: 'configuration' })}
                            </Label>
                            <span>
                                <Input
                                    disabled={!props.configuration.onlyIncludeGovernedByMunicipal || !props.configuration.onlyIncludeSchoolsWithMunicipalityCode || props.disabled}
                                    invalid={validation.indexOf('municipalityCode') !== -1}
                                    type="text"
                                    placeholder={t('edlevoConfiguration.schools.onlyIncludeSchoolsWithMunicipalityCodePlaceholder', { ns: 'configuration' })}
                                    name="municipalityCode" id="municipalityCode"
                                    value={props.configuration.municipalityCode || ''}
                                    onChange={(e) => updateMunicipalitySchool('municipalityCode', e.target.value)}
                                />
                                <FormFeedback>{t('edlevoConfiguration.schools.formFeedback', { ns: 'configuration' })} </FormFeedback>
                            </span>
                        </FormGroup>
                    </FormGroup>

                    <EnumInput
                        multi={true}
                        title={t('edlevoConfiguration.schools.enumTitle', { ns: 'configuration' })}
                        titleSmall={true}
                        onChange={(name, value) => props.updatePart({ [name]: value })}
                        name="schoolTypes"
                        value={props.configuration.schoolTypes}
                        options={EdlevoSchoolTypeOptions.map(key => ({
                            value: key,
                            text: t(`schoolTypes.${key}`, { ns: 'common' })
                        }))}
                        disabled={props.disabled}
                        invalid={validation.indexOf('schoolTypes') !== -1}
                        invalidFeedback={t('edlevoConfiguration.schools.enumInvalidFeedback', { ns: 'configuration' })}
                    />
                </CardBody>
            </Collapse>
        </Card >)

}

export default EdlevoConfiguration;
